import { AfterViewInit, Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { OrderApiService } from '~services/api/order.api.service';
import { ErrorHandlingService } from '~services/error-handling.service';
import { UserService } from '~services/user.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { TruckingVendor } from '~lmo/models/trucking-vendor.model';
import { LmoFracsService, LmoTruckingVendorsService } from '~lmo/services';
import { debounceTime, filter, map, startWith, take } from 'rxjs/operators';
import { fuse } from '~utilities/fuse';
import Handsontable from 'handsontable';
import { animate, style, transition, trigger } from '@angular/animations';
import { CrudService } from '~services/crud.service';
import { Frac } from '~lmo/models/frac.model';
import { WellApiService } from '~services/api/well.api.service';

export interface ColumnView {
  open: boolean;
  columns: ColumnsObject[];
}

export interface FormValue {
  startDate: Date;
  endDate: Date;
  email: boolean;
  exportBy: boolean;
  vendors: TruckingVendor[];
  fracs: Frac[];
}

export interface ColumnsObject {
  id: number;
  name: string;
  checked: boolean;
  position: number;
}

export interface ColumnsTemplate {
  id: number;
  templateName: string;
  columns: string;
}

enum ExportBy {
  DispatchTimestamp = 'dispatch_timestamp',
  ApprovalTimestamp = 'approval_timestamp',
  CompletedAtTimestamp = 'completed_at_timestamp',
}

const searchOptions: Fuse.FuseOptions<TruckingVendor> = {
  distance: 100,
  keys: ['name'],
  location: 0,
  maxPatternLength: 16,
  minMatchCharLength: 1,
  shouldSort: true,
  threshold: 0.2,
};

const fracSearchOptions: Fuse.FuseOptions<Frac> = {
  distance: 100,
  keys: ['site.name'],
  location: 0,
  maxPatternLength: 16,
  minMatchCharLength: 1,
  shouldSort: true,
  threshold: 0.2,
};

@Component({
  selector: 'sa-bulk-export-v2',
  templateUrl: './bulk-export-v2.component.html',
  styleUrls: ['./bulk-export-v2.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' })),
      ]),
      transition(':leave', [animate('200ms ease-in', style({ transform: 'translateX(100%)' }))]),
    ]),
  ],
})
export class BulkExportV2Component implements OnInit, AfterViewInit {
  public downloadDataForm: FormGroup;
  public todayDate;
  public isDownloading = false;
  public isFetchingPreview = false;
  public isUpdating = false;
  public isApplying = false;
  public allVendors$: Observable<TruckingVendor[]>;
  public vendorFilter = new FormControl();
  public allFracs$: Observable<Frac[]>;
  public fracFilter = new FormControl();
  public columns: BehaviorSubject<ColumnsObject[]> = new BehaviorSubject<ColumnsObject[]>(null);
  public tempSelectedTemplate: BehaviorSubject<ColumnsTemplate> = new BehaviorSubject<ColumnsTemplate>(null);
  public tableData: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(null);
  public userEmail: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public fetchExportColumnTemplate: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public newlyAddedTemplateId: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  public showUpdateTemplateButton: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public ExportBy = ExportBy;
  private loadsTable: Handsontable;
  @ViewChild('previewTableContainer') private previewTableContainer: ElementRef;
  @Output() onCloseClicked: EventEmitter<void> = new EventEmitter<void>();

  public get formValue(): FormValue {
    return this.downloadDataForm.value as FormValue;
  }

  columnView: ColumnView = {
    open: false,
    columns: [],
  };

  constructor(
    private fb: FormBuilder,
    private orderApiService: OrderApiService,
    private errorHandler: ErrorHandlingService,
    private userService: UserService,
    private lmoTruckingVendorService: LmoTruckingVendorsService,
    private fracService: LmoFracsService,
    private wellApiService: WellApiService,
    private snackBar: MatSnackBar,
    private crudService: CrudService,
  ) {}

  ngOnInit() {
    this.todayDate = new Date();
    this.setupForm();
    this.setEmail();
    this.setupInitialColumns();
  }

  public ngAfterViewInit(): void {
    this.setUpTable();
  }

  private setEmail() {
    const userContract = this.userService.getUserContractFromCache();
    this.userEmail.next(userContract.email);
  }

  public setShowUpdateTemplateButton(b: boolean): void {
    this.showUpdateTemplateButton.next(b);
  }

  private setupInitialColumns() {
    const body = {
      startTime: new Date(),
      endTime: new Date(),
      email: false,
      exportBy: ExportBy.DispatchTimestamp,
      vendorIds: [],
    };

    this.crudService.httpClientReady.pipe(filter(Boolean), take(1)).subscribe((ready) => {
      if (ready) {
        const endpoint = this.userService.isLMOAccount()
          ? this.orderApiService.getAllLmoData(body)
          : this.orderApiService.getAllVendorData(body);
        endpoint.subscribe((data) => {
          this.columns.next(data[0].map((item) => ({ name: item, checked: true })));
        });
      }
    });
    this.downloadDataForm.markAsDirty();
  }

  public setUpTable() {
    this.loadsTable = new Handsontable(this.previewTableContainer.nativeElement, {
      readOnly: true,
      rowHeaders: true,
      colHeaders: true,
      licenseKey: 'non-commercial-and-evaluation',
      search: true,
      manualColumnMove: true,
      width: '100%',
      height: 'auto',
      afterColumnMove: (movedColumns: number[], finalIndex: number) => {
        const arrangedArray: ColumnsObject[] = this.loadsTable.getColHeader().map((name) => {
          const item = this.columns.value.find((obj) => obj.name === name);
          return item
            ? { id: item.id, name: item.name as string, checked: item.checked, position: item.position }
            : { id: item.id, name: name as string, checked: false, position: item.position };
        });

        this.columns.next(arrangedArray);
        if (this.tempSelectedTemplate.value) {
          this.showUpdateTemplateButton.next(true);
        } else {
          this.showUpdateTemplateButton.next(false);
        }
      },
    });
  }

  public updateTemplateArrangement() {
    if (!this.tempSelectedTemplate.value) {
      this.snackBar.open('Please select a template first', null, { duration: 2000, panelClass: ['snackbar-error'] });
      return;
    }
    this.isUpdating = true;
    const visibleColumns = [];

    for (let i = 0; i < this.loadsTable.countCols(); i++) {
      if (!this.loadsTable.getPlugin('hiddenColumns').isHidden(i)) {
        visibleColumns.push(this.loadsTable.getColHeader(i));
      }
    }

    let positionCounter = 1;
    visibleColumns.forEach((item, index) => {
      const match = this.columns.value.find((c) => c.name === item);
      if (match) {
        match.position = positionCounter++;
      }
    });

    this.columns.value
      .filter((sd) => !visibleColumns.includes(sd.name))
      .forEach((item) => {
        item.position = positionCounter++;
      });

    this.columns.value.sort((a, b) => a.position - b.position);

    const template: ColumnsTemplate = {
      id: this.tempSelectedTemplate.value.id,
      templateName: this.tempSelectedTemplate.value.templateName,
      columns: JSON.stringify(this.columns.value),
    };

    const body = {
      templateName: template.templateName,
      columns: template.columns,
    };

    this.orderApiService.updateTemplate(body, template.id).subscribe((data) => {
      if (data && data > 0) {
        this.fetchExportColumnTemplate.next(true);
        this.snackBar.open(`Successfully updated template`, null, { duration: 5000 });
      }
      this.isUpdating = false;
    });
    this.fetchExportColumnTemplate.next(false);
    this.columns.next(this.columns.value);
  }

  private setupForm() {
    this.allVendors$ = combineLatest([
      this.lmoTruckingVendorService.vendorsWithContract$.pipe(map(fuse<TruckingVendor>(searchOptions))),
      this.vendorFilter.valueChanges.pipe(debounceTime(100), startWith('')),
    ]).pipe(
      map(([fused, searchTerm]) => {
        if (!searchTerm || searchTerm === '') {
          return fused.data;
        }
        return fused.fuse.search(searchTerm) as any;
      }),
    );

    const fracList: Observable<Frac[]> = combineLatest([this.fracService.fracs$, this.fracService.archivedFracs$]).pipe(
      map(([firstValues, secondValues]) => [...firstValues, ...secondValues]),
    );

    this.allFracs$ = combineLatest([
      fracList.pipe(map(fuse<Frac>(fracSearchOptions))),
      this.fracFilter.valueChanges.pipe(debounceTime(100), startWith('')),
    ]).pipe(
      map(([fused, searchTerm]) => {
        if (!searchTerm || searchTerm === '') {
          return fused.data;
        }
        return fused.fuse.search(searchTerm) as any;
      }),
    );

    this.downloadDataForm = this.fb.group({
      startDate: [this.todayDate, [Validators.required]],
      endDate: [this.todayDate, [Validators.required]],
      email: [false],
      exportBy: [ExportBy.DispatchTimestamp, [Validators.required]],
      vendors: [null],
      fracs: [null],
    });
  }

  public fetchPreviewData(updatedColumns: ColumnsObject[]) {
    if (updatedColumns) {
      this.isApplying = true;
    } else {
      this.isFetchingPreview = true;
    }
    const value = this.formValue;
    const startTime = value.startDate.setHours(0, 0, 0);
    const endTime = value.endDate.setHours(23, 59, 59);
    const selectedVendorIds = value?.vendors?.map((vendor) => vendor.id) ?? [];
    const selectedFracIds = value?.fracs?.map((frac) => frac.id) ?? [];
    let selectedHeadersName = this.columns.value.map((item) => item.name);
    if (updatedColumns) {
      selectedHeadersName = updatedColumns.map((item) => item.name);
    }
    const body = {
      startTime: new Date(startTime),
      endTime: new Date(endTime),
      email: value.email,
      exportBy: this.downloadDataForm.get('exportBy').value,
      vendorIds: selectedVendorIds,
      columnToShow: selectedHeadersName,
      fracIds: selectedFracIds,
    };

    const endpoint = this.userService.isLMOAccount()
      ? this.orderApiService.getAllLmoData(body)
      : this.orderApiService.getAllVendorData(body);
    endpoint.subscribe(
      (data) => {
        this.tableData.next(data);
        const headersName = this.columns.value.map((item) => item.name);
        this.loadsTable.updateSettings({
          colHeaders: headersName,
          height: 400,
        });
        data.shift();
        data = data.map((row) => row.map((item) => item.replace(/^"|"$/g, '')));
        this.loadsTable.loadData(data);
        if (updatedColumns) {
          this.loadsTable.updateSettings({
            hiddenColumns: {
              columns: updatedColumns
                .map((column, index) => (column.checked ? -1 : index))
                .filter((index) => index !== -1),
            },
          });
          this.snackBar.open('Columns have been applied successfully', null, { duration: 2000 });
        }
        this.isFetchingPreview = false;
        this.isApplying = false;
        this.downloadDataForm.markAsPristine();
      },
      (err) => {
        this.isFetchingPreview = false;
        this.errorHandler.showError(err);
      },
    );
  }

  public downloadData() {
    const value = this.formValue;
    const startTime = value.startDate.setHours(0, 0, 0);
    const endTime = value.endDate.setHours(23, 59, 59);
    if (!value.email) {
      this.isDownloading = true;
    }
    const selectedVendorIds = value?.vendors?.map((vendor) => vendor.id) ?? [];
    const selectedFracIds = value?.fracs?.map((frac) => frac.id) ?? [];
    const selectedHeadersName = this.columns.value.filter((item) => item.checked).map((item) => item.name);
    const body = {
      startTime: new Date(startTime),
      endTime: new Date(endTime),
      email: value.email,
      exportBy: this.downloadDataForm.get('exportBy').value,
      vendorIds: selectedVendorIds,
      columnToShow: selectedHeadersName,
      fracIds: selectedFracIds,
    };

    const endpoint = this.userService.isLMOAccount()
      ? this.orderApiService.downloadAllLmoData(body)
      : this.orderApiService.downloadAllVendorData(body);
    endpoint.subscribe(
      (data) => {
        if (!value.email) {
          window.open(data, '_blank');
          this.isDownloading = false;
          this.snackBar.open('successfully downloaded the data', null, {
            duration: 5000,
          });
        } else {
          this.snackBar.open('your bulk export will be emailed to you shortly', null, {
            duration: 5000,
          });
        }
      },
      (err) => {
        this.isDownloading = false;
        this.errorHandler.showError(err);
      },
    );
  }

  closeClicked() {
    this.columnView.open = false;
    this.columnView.columns = [];
    this.newlyAddedTemplateId.next(null);
  }

  openColumnView() {
    this.columnView.open = true;
    this.columnView.columns = this.columns.value;
  }

  updateTableHeaderColumn(updatedColumns: ColumnsObject[]) {
    this.fetchPreviewData(updatedColumns);
  }

  saveTempColumn(updatedColumns: ColumnsObject[]) {
    this.columns.next(updatedColumns);
  }

  saveTempSelectedTemplate(columnsTemplate: ColumnsTemplate) {
    this.tempSelectedTemplate.next(columnsTemplate);
  }

  addNewTemplate(columnsTemplate: ColumnsTemplate) {
    const body = {
      templateName: columnsTemplate.templateName['templateName'],
      columns: columnsTemplate.columns,
    };

    this.orderApiService.addNewTemplate(body).subscribe((data) => {
      if (data && data > 0) {
        this.fetchExportColumnTemplate.next(true);
        this.newlyAddedTemplateId.next(data);
        this.snackBar.open(`Successfully created new template`, null, { duration: 5000 });
      }
    });
    this.fetchExportColumnTemplate.next(false);
  }

  updateTemplate(columnsTemplate: ColumnsTemplate) {
    const body = {
      templateName: columnsTemplate.templateName['templateName'],
      columns: columnsTemplate.columns,
    };

    this.orderApiService.updateTemplate(body, columnsTemplate.id).subscribe((data) => {
      if (data && data > 0) {
        this.fetchExportColumnTemplate.next(true);
        this.newlyAddedTemplateId.next(data);
        this.snackBar.open(`Successfully updated template`, null, { duration: 5000 });
      }
    });
    this.newlyAddedTemplateId.next(null);
    this.fetchExportColumnTemplate.next(false);
  }

  deleteTemplate(templateId: number) {
    this.orderApiService.deleteTemplate(templateId).subscribe((data) => {
      if (data && data > 0) {
        this.fetchExportColumnTemplate.next(true);
        this.snackBar.open(`Successfully deleted template`, null, { duration: 5000 });
      }
    });
    this.fetchExportColumnTemplate.next(false);
  }
}
